import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { isOrganizationAdmin } from '@actions/selectors';
import Card from '@components/Card';
import Select from '@components/Select';
import useAPI from '@hooks/useAPI';
import Button from '@shared/Button';
import Modal from '@shared/Modal';
import Spinner from '@shared/Spinner';
import ErrorMessage from '@src/components/Input/ErrorMessage';

import {
  getAccessibleEnvironments,
  getCurrentBackendEnv,
  getEnvLabel,
  ModalContainer,
  SummarySection,
  ErrorItem,
  ErrorRuleName,
  ErrorDetail,
  DirectionLabel,
  CloseButtonRow,
  DiffSummary,
  ValidationErrors,
  RawJsonView,
  getValidationErrors,
} from './RuleDiffShared';

/* ── Helpers ── */

// True when a diff object ({ updated_rules, removed_rules }) describes at
// least one actual change. Mirrors the check DiffSummary uses to decide
// between rendering the summary and the "no changes" message.
const diffHasChanges = diff => {
  if (!diff) return false;
  const updatedCount = diff.updated_rules
    ? Object.keys(diff.updated_rules).length
    : 0;
  const removedCount = diff.removed_rules ? diff.removed_rules.length : 0;
  return updatedCount > 0 || removedCount > 0;
};

/* ── Layout styled components ── */

const Container = styled.div`
  width: 70%;
  margin: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const SelectContainer = styled.div`
  width: 250px;
`;

const FileName = styled.span`
  font-size: 14px;
  color: #555;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 16px 0;
`;

const CurrentEnvLabel = styled.span`
  font-size: 13px;
  color: #555;
`;

const WarningBanner = styled.div`
  background: #fff3e0;
  border-left: 4px solid #ff9800;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 0 4px 4px 0;
  font-size: 14px;
  color: #e65100;
  font-weight: 500;
`;

const UnauthorizedBanner = styled.div`
  background: #fdecea;
  border-left: 4px solid #d32f2f;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 0 4px 4px 0;
  font-size: 14px;
  color: #b71c1c;
  font-weight: 500;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  border-bottom: 2px solid #e0e0e0;
`;

const Tab = styled.button`
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  outline: none;
  cursor: pointer;
  background: ${props => (props.$active ? '#fff' : 'transparent')};
  color: ${props => (props.$active ? '#1976d2' : '#888')};
  border-bottom: ${props =>
    props.$active ? '2px solid #1976d2' : '2px solid transparent'};
  margin-bottom: -2px;
  transition: color 0.15s;

  &:hover {
    color: ${props => (props.$active ? '#1976d2' : '#555')};
  }
`;

/* ── Confirmation modal styled components ── */

const ConfirmButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 0 0 0;
`;

const SuccessBanner = styled.div`
  background: #e8f5e9;
  border-left: 4px solid #4caf50;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 0 4px 4px 0;
  font-size: 14px;
  color: #2e7d32;
  font-weight: 500;
`;

const TimerText = styled.span`
  font-size: 13px;
  color: #888;
  margin-left: 8px;
`;

/* ────────────────────────────────────────────
 *  Confirmation modal — shared between both paths
 * ──────────────────────────────────────────── */

function ConfirmApplyModal({
  title,
  previewContent,
  hasPreview,
  canApply,
  isAuthorized = false,
  isPreviewLoading,
  previewError,
  applyResponse,
  isApplyLoading,
  applyError,
  applyErrorData,
  onConfirm,
  onClose,
}) {
  const [countdown, setCountdown] = useState(5);
  const timerRef = useRef(null);

  const applied = !!applyResponse;
  // A failed apply can come back carrying validation errors (under `details`
  // or `errors`). Surface those the same way as the preview phase instead of
  // a generic error string.
  const applyValidationErrors = getValidationErrors(applyErrorData);
  // `hasPreview` is an explicit boolean intent supplied by the caller — it
  // says "preview data has arrived" without depending on the identity of the
  // rendered `previewContent` element (which changes every render and would
  // otherwise restart the countdown effect).
  const canShowPreview = hasPreview && !previewError;

  // Start the 5-second countdown once preview data with actual changes
  // arrives. No countdown when there is nothing to apply — the Confirm
  // button stays disabled in that case.
  useEffect(() => {
    if (canShowPreview && canApply && !applied && !isApplyLoading) {
      setCountdown(5);
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [canShowPreview, canApply, applied, isApplyLoading]);

  return (
    <Modal
      id='confirm-apply-modal'
      open
      toggle={onClose}
      closeOnClickOutside={!isApplyLoading}
      maxWidth={900}
    >
      <Card title={title} shadow>
        <ModalContainer>
          {/* ── Preview phase ── */}
          {isPreviewLoading && <Spinner center />}

          {previewError && !previewContent && (
            <SummarySection>
              <ErrorItem>
                <ErrorRuleName>Preview Error</ErrorRuleName>
                <ErrorDetail>{previewError}</ErrorDetail>
              </ErrorItem>
            </SummarySection>
          )}

          {previewContent}

          {/* ── Apply phase ── */}
          {isApplyLoading && (
            <SummarySection>
              <Spinner center />
            </SummarySection>
          )}

          {applyValidationErrors && (
            <ValidationErrors errors={applyValidationErrors} />
          )}

          {applyError && !applyValidationErrors && (
            <SummarySection>
              <ErrorItem>
                <ErrorRuleName>Apply Error</ErrorRuleName>
                <ErrorDetail>{applyError}</ErrorDetail>
              </ErrorItem>
            </SummarySection>
          )}

          {applied && !applyError && (
            <SuccessBanner>Rules applied successfully.</SuccessBanner>
          )}

          {/* ── Action buttons ── */}
          <ConfirmButtonRow>
            {!applied && canShowPreview && !isApplyLoading && (
              <Button
                onClick={onConfirm}
                disabled={
                  !canApply || countdown > 0 || !!applyError || !isAuthorized
                }
                style={{
                  background:
                    !canApply || countdown > 0 || applyError || !isAuthorized
                      ? '#ccc'
                      : '#d32f2f',
                  color: 'white',
                }}
              >
                {canApply && countdown > 0
                  ? `Confirm (${countdown}s)`
                  : 'Confirm Apply'}
              </Button>
            )}
            {!applied &&
              canShowPreview &&
              canApply &&
              !isApplyLoading &&
              countdown > 0 && (
                <TimerText>
                  Please review the changes above before confirming.
                </TimerText>
              )}
            {!applied && canShowPreview && !canApply && !isApplyLoading && (
              <TimerText>No rules to apply.</TimerText>
            )}
            {!applied && !isAuthorized && (
              <TimerText>
                You are not authorized to apply rule changes.
              </TimerText>
            )}
            <Button onClick={onClose} disabled={isApplyLoading}>
              {applied ? 'Done' : 'Cancel'}
            </Button>
          </ConfirmButtonRow>
        </ModalContainer>
      </Card>
    </Modal>
  );
}

/* ────────────────────────────────────────────
 *  Tab 1 — Upload rules from file
 * ──────────────────────────────────────────── */

function UploadRulesTab({ isAuthorized = false }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedRules, setParsedRules] = useState(null);
  const [parseError, setParseError] = useState('');

  // Preview diff (preview_diff endpoint)
  const [previewParams, setPreviewParams] = useState([]);
  const previewResponse = useAPI(...previewParams);

  // Apply (PUT /config/<id>/rules)
  const [applyParams, setApplyParams] = useState([]);
  const applyResponse = useAPI(...applyParams);

  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (!file) return;

    setParseError('');
    setParsedRules(null);
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const json = JSON.parse(e.target.result);
        if (!Array.isArray(json)) {
          setParseError(
            'Invalid format: the JSON file must contain an array of rule objects.'
          );
          return;
        }
        setParsedRules(json);
      } catch (err) {
        setParseError(`Failed to parse JSON: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handleApplyClick = () => {
    if (!parsedRules) return;
    setShowModal(true);
    setApplyParams([]);
    setPreviewParams([
      'post',
      'ScreenConsoleApi',
      `config/${process.env.REACT_APP_CUSTOMER_ID}/rules/preview_diff`,
      Date.now().toString(),
      false,
      '',
      false,
      parsedRules,
    ]);
  };

  const handleConfirm = () => {
    setApplyParams([
      'put',
      'ScreenConsoleApi',
      `config/${process.env.REACT_APP_CUSTOMER_ID}/rules`,
      Date.now().toString(),
      false,
      '',
      false,
      parsedRules,
    ]);
  };

  const handleClose = () => {
    setShowModal(false);
    setPreviewParams([]);
    setApplyParams([]);
  };

  const handleClear = () => {
    setSelectedFile(null);
    setParsedRules(null);
    setParseError('');
    setPreviewParams([]);
    setApplyParams([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Build preview content for the modal.
  // Errors now arrive as a flat array of strings under `details`, and a
  // validation failure comes back with a 4xx status — so the body lands in
  // useAPI's `errorData` rather than `response`.
  const previewData = previewResponse.response || previewResponse.errorData;
  const validationErrors = getValidationErrors(previewData);
  const hasValidationErrors = !!validationErrors;
  const hasDiff = previewData && !hasValidationErrors && previewData.diff;

  let previewContent = null;
  if (hasValidationErrors) {
    previewContent = <ValidationErrors errors={validationErrors} />;
  } else if (hasDiff) {
    previewContent = (
      <>
        <DiffSummary
          diff={previewData.diff}
          emptyMessage='No rule changes detected. The uploaded rules match the current configuration.'
        />
        <RawJsonView src={previewData.diff} />
      </>
    );
  }

  // Only allow applying when the preview validated AND yielded real changes.
  const canApply = hasDiff && diffHasChanges(previewData.diff);

  return (
    <>
      {parseError && <ErrorMessage error={parseError} />}

      <Row>
        <Button onClick={handleFileSelect}>Choose rules file…</Button>
        <FileName>
          {selectedFile ? selectedFile.name : 'No file selected'}
        </FileName>
        <input
          type='file'
          accept='.json'
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Row>

      <Row>
        <Button
          onClick={handleApplyClick}
          disabled={!parsedRules || !isAuthorized}
        >
          Upload &amp; Apply Rules
        </Button>
        {selectedFile && (
          <Button
            onClick={handleClear}
            style={{
              background: 'transparent',
              color: '#888',
              border: '1px solid #ccc',
            }}
          >
            Clear
          </Button>
        )}
      </Row>

      {showModal && (
        <ConfirmApplyModal
          title={
            hasValidationErrors
              ? 'Apply Rules from File — Validation Failed'
              : 'Apply Rules from File'
          }
          previewContent={previewContent}
          hasPreview={!!hasValidationErrors || !!hasDiff}
          canApply={canApply}
          isAuthorized={isAuthorized}
          isPreviewLoading={previewResponse.isLoading}
          previewError={previewResponse.error}
          applyResponse={applyResponse.response}
          isApplyLoading={applyResponse.isLoading}
          applyError={applyResponse.error}
          applyErrorData={applyResponse.errorData}
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      )}
    </>
  );
}

/* ────────────────────────────────────────────
 *  Tab 2 — Import rules from another environment
 * ──────────────────────────────────────────── */

function ImportEnvTab({ isAuthorized = false }) {
  const currentEnv = getCurrentBackendEnv();
  const currentLabel = getEnvLabel(currentEnv);

  const sourceOptions = getAccessibleEnvironments().filter(
    ([key]) => key !== currentEnv
  );

  const [selectedSource, setSelectedSource] = useState('');

  // Preview diff (diff endpoint)
  const [diffParams, setDiffParams] = useState([]);
  const diffResponse = useAPI(...diffParams);

  // Apply (migrate endpoint)
  const [migrateParams, setMigrateParams] = useState([]);
  const migrateResponse = useAPI(...migrateParams);

  const [showModal, setShowModal] = useState(false);

  const handleImportClick = () => {
    if (!selectedSource) return;
    setShowModal(true);
    setMigrateParams([]);
    setDiffParams([
      'post',
      'ScreenConsoleApi',
      `config/${process.env.REACT_APP_CUSTOMER_ID}/rules/diff`,
      Date.now().toString(),
      false,
      '',
      false,
      { source: selectedSource },
    ]);
  };

  const handleConfirm = () => {
    setMigrateParams([
      'post',
      'ScreenConsoleApi',
      `config/${process.env.REACT_APP_CUSTOMER_ID}/rules/migrate`,
      Date.now().toString(),
      false,
      '',
      false,
      { source: selectedSource },
    ]);
  };

  const handleClose = () => {
    setShowModal(false);
    setDiffParams([]);
    setMigrateParams([]);
  };

  // Build preview content for the modal.
  // Unified response shape: { status, details, diff }. Validation failures
  // come back with a 4xx carrying { status, details }, so read useAPI's
  // `errorData` too; the diff lives under `diff` on success.
  const responseData = diffResponse.response || diffResponse.errorData;
  const validationErrors = getValidationErrors(responseData);
  const hasValidationErrors = !!validationErrors;
  // The /rules/diff endpoint returns a flat payload
  // ({ source, destination, updated_rules, removed_rules }) — there is no
  // `diff` wrapper like preview_diff has, so fall back to the response itself.
  const diff =
    responseData && !hasValidationErrors
      ? responseData.diff || responseData
      : null;
  const hasDiff =
    !!diff &&
    (diff.updated_rules !== undefined || diff.removed_rules !== undefined);

  const sourceLabel = getEnvLabel(selectedSource);

  let previewContent = null;
  if (hasValidationErrors) {
    previewContent = <ValidationErrors errors={validationErrors} />;
  } else if (hasDiff) {
    previewContent = (
      <>
        <DirectionLabel>
          Importing rules from{' '}
          <strong>{diff.source || sourceLabel}</strong> into{' '}
          <strong>{diff.destination || currentLabel}</strong>
        </DirectionLabel>
        <DiffSummary
          diff={diff}
          emptyMessage='No rule differences found between the two environments.'
        />
        <RawJsonView src={diff} />
      </>
    );
  }

  // Only allow applying when the diff yielded real changes.
  const canApply = hasDiff && diffHasChanges(diff);

  return (
    <>
      <Row>
        <SelectContainer>
          <Select
            id='import-env-source-select'
            options={sourceOptions}
            value={selectedSource}
            onChange={e => setSelectedSource(e.target.value)}
            placeholder='Select source environment'
            style={{ height: '43px' }}
          />
        </SelectContainer>
        <CurrentEnvLabel>
          → Current environment: <strong>{currentLabel}</strong>
        </CurrentEnvLabel>
      </Row>

      <Row>
        <Button
          onClick={handleImportClick}
          disabled={!selectedSource || !isAuthorized}
        >
          Import &amp; Apply Rules
        </Button>
      </Row>

      {showModal && (
        <ConfirmApplyModal
          title='Import Rules from Environment'
          previewContent={previewContent}
          hasPreview={!!hasValidationErrors || !!hasDiff}
          canApply={canApply}
          isAuthorized={isAuthorized}
          isPreviewLoading={diffResponse.isLoading}
          previewError={diffResponse.error}
          applyResponse={migrateResponse.response}
          isApplyLoading={migrateResponse.isLoading}
          applyError={migrateResponse.error}
          applyErrorData={migrateResponse.errorData}
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      )}
    </>
  );
}

/* ────────────────────────────────────────────
 *  Main export — tabbed container
 * ──────────────────────────────────────────── */

export default function ApplyRules() {
  const [activeTab, setActiveTab] = useState('file');

  // Applying rules is a privileged action — gate it behind the organization
  // admin/owner roles, matching the Thresholds / Sanction Lists update guards.
  const isAuthorized = useSelector(isOrganizationAdmin);

  return (
    <Container>
      <WarningBanner>
        Actions in this section will modify rules in the current environment.
        Changes take effect immediately once confirmed.
      </WarningBanner>

      {!isAuthorized && (
        <UnauthorizedBanner>
          You do not have permission to apply rule changes. Applying rules
          requires the Admin role — please request access if you need to make
          changes.
        </UnauthorizedBanner>
      )}

      <TabRow>
        <Tab
          $active={activeTab === 'file'}
          onClick={() => setActiveTab('file')}
        >
          Upload from File
        </Tab>
        <Tab
          $active={activeTab === 'env'}
          onClick={() => setActiveTab('env')}
        >
          Import from Environment
        </Tab>
      </TabRow>

      {activeTab === 'file' && (
        <>
          <HelpText>
            Upload a rules JSON file to replace the current rules in this
            environment.
          </HelpText>
          <UploadRulesTab isAuthorized={isAuthorized} />
        </>
      )}

      {activeTab === 'env' && (
        <>
          <HelpText>
            Select a source environment to copy its rules into this
            environment, replacing the current rules.
          </HelpText>
          <ImportEnvTab isAuthorized={isAuthorized} />
        </>
      )}
    </Container>
  );
}
